import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useTennesseeMemphisDemoEnabled } from 'utils/featureFlags';
import {
  TNNG_MEMPHIS_ORG_ACTION_DESCRIPTION,
  TNNG_MEMPHIS_RESPONSE_CHECKLIST_LABELS,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/tennesseeMemphisThreatNotification';
import {
  keys as orgActionKeys,
  type GetOrgActionResponse,
} from 'components/IAP/hooks/orgActionHooks';

const ensureLocks = new Map<string, Promise<unknown>>();

async function ensureTennesseeMemphisOrgActionsForGroup({
  groupGuid,
  userGuid,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<void> {
  const resp = await axios.get<GetOrgActionResponse[]>(
    `${config.apiGateway.ics_org_actions}/${groupGuid}`
  );
  const existingNames = new Set(
    (resp.data ?? [])
      .filter((action) => !action.archived)
      .map((action) => action.name)
  );

  const missing = TNNG_MEMPHIS_RESPONSE_CHECKLIST_LABELS.filter(
    (label) => !existingNames.has(label)
  );
  if (missing.length === 0) {
    return;
  }

  await Promise.all(
    missing.map((name) =>
      axios.post(`${config.apiGateway.ics_org_actions}/${groupGuid}`, {
        name,
        description: TNNG_MEMPHIS_ORG_ACTION_DESCRIPTION,
        checklist: [],
        status: 'Planned',
        user_guid: userGuid,
        utc_offset: -new Date().getTimezoneOffset(),
      })
    )
  );

  await queryClient.invalidateQueries({
    queryKey: orgActionKeys.byGroupAndUser(groupGuid, userGuid),
  });
}

/** When TNG-only, ensure flood/outage COA labels exist as Planned org actions. */
export function useEnsureTennesseeMemphisOrgActions() {
  const tennesseeMemphisDemo = useTennesseeMemphisDemoEnabled();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!tennesseeMemphisDemo || !groupGuid || !userGuid) {
      return;
    }

    const scopeKey = `${groupGuid}:${userGuid}`;
    if (completedRef.current === scopeKey) {
      return;
    }

    let cancelled = false;
    let ensurePromise = ensureLocks.get(scopeKey);
    if (!ensurePromise) {
      ensurePromise = ensureTennesseeMemphisOrgActionsForGroup({
        groupGuid,
        userGuid,
        queryClient,
      })
        .catch((error) => {
          console.error(
            '[TNNG Memphis] Failed to ensure Response Checklist org actions',
            error
          );
          throw error;
        })
        .finally(() => {
          ensureLocks.delete(scopeKey);
        });
      ensureLocks.set(scopeKey, ensurePromise);
    }

    void ensurePromise
      .then(() => {
        if (!cancelled) {
          completedRef.current = scopeKey;
        }
      })
      .catch(() => {
        // Allow retry on next effect.
      });

    return () => {
      cancelled = true;
    };
  }, [tennesseeMemphisDemo, groupGuid, userGuid, queryClient]);
}
